import { initializeApp, getApps, getApp, deleteApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

class FirebaseSingleton {
  constructor() {
    this.app = null;
    this.auth = null;
    this.firestore = null;
    this.initializationPromise = null;
    this.isInitializing = false;
  }

  async initialize(firebaseConfig, options = {}) {
    if (this.isInitialized()) {
      return this.app;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.isInitializing = true;
    this.initializationPromise = new Promise(async (resolve, reject) => {
      try {
        console.log('[FirebaseSingleton] Starting Firebase initialization...');

        const existingApps = getApps();
        if (existingApps.length > 0) {
          console.log('[FirebaseSingleton] Using existing Firebase app.');
          this.app = getApp();
        } else {
          if (!firebaseConfig?.apiKey || !firebaseConfig?.projectId) {
            throw new Error('Invalid Firebase config: apiKey and projectId are required.');
          }
          console.log('[FirebaseSingleton] Initializing new Firebase app.');
          this.app = initializeApp(firebaseConfig);
        }

        this.auth = getAuth(this.app);
        const persistenceType = options.persistence || browserLocalPersistence;
        
        await setPersistence(this.auth, persistenceType);
        console.log(`[FirebaseSingleton] Auth persistence set to: ${persistenceType === browserLocalPersistence ? 'LOCAL' : 'SESSION'}`);

        this.firestore = getFirestore(this.app);
        console.log('[FirebaseSingleton] Firestore initialized.');
        console.log('[FirebaseSingleton] Firebase initialization complete.');

        this.isInitializing = false;
        resolve(this.app);
      } catch (error) {
        console.error('[FirebaseSingleton] Initialization error:', error);
        await this.resetInitialization();
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  async resetInitialization() {
    console.warn('[FirebaseSingleton] Resetting Firebase initialization state.');
    this.app = null;
    this.auth = null;
    this.firestore = null;
    this.initializationPromise = null;
    this.isInitializing = false;
    // This is a more forceful reset for complex HMR or testing scenarios
    const apps = getApps();
    if (apps.length > 0) {
      try {
        await deleteApp(apps);
        console.log('[FirebaseSingleton] Existing Firebase app deleted.');
      } catch (e) {
        console.error('[FirebaseSingleton] Error deleting existing app:', e);
      }
    }
  }

  isInitialized() {
    return !!(this.app && this.auth && this.firestore);
  }

  getApp() {
    if (!this.app) throw new Error('[FirebaseSingleton] Firebase app not initialized. Call initialize() first.');
    return this.app;
  }

  getAuthInstance() {
    if (!this.auth) throw new Error('[FirebaseSingleton] Auth not initialized. Call initialize() first.');
    return this.auth;
  }

  getFirestoreInstance() {
    if (!this.firestore) throw new Error('[FirebaseSingleton] Firestore not initialized. Call initialize() first.');
    return this.firestore;
  }

  async signInWithCustomToken(token) {
    if (!token) throw new Error('Token is required for custom sign-in.');
    try {
      console.log('[FirebaseSingleton] Attempting custom token sign-in.');
      const auth = this.getAuthInstance();
      const userCredential = await signInWithCustomToken(auth, token);
      console.log('[FirebaseSingleton] Custom token sign-in successful.');
      return userCredential;
    } catch (error) {
      console.error('[FirebaseSingleton] Custom token sign-in failed:', error);
      throw error;
    }
  }

  async signOut() {
    try {
      console.log('[FirebaseSingleton] Signing out.');
      const auth = this.getAuthInstance();
      await signOut(auth);
      console.log('[FirebaseSingleton] Sign-out successful.');
    } catch (error) {
      console.error('[FirebaseSingleton] Sign-out failed:', error);
      throw error;
    }
  }

  onAuthStateChanged(callback) {
    if (typeof callback !== 'function') throw new Error('Callback must be a function.');
    try {
      const auth = this.getAuthInstance();
      return onAuthStateChanged(auth, (user) => {
        callback(user);
      }, (error) => {
        console.error('[FirebaseSingleton] Auth state change error:', error);
        callback(null, error);
      });
    } catch (error) {
      console.error('[FirebaseSingleton] Failed to set up auth state listener:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const auth = this.getAuthInstance();
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          (user) => {
            unsubscribe();
            resolve(user);
          },
          (error) => {
            unsubscribe();
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('[FirebaseSingleton] Get current user failed:', error);
      throw error;
    }
  }
}

const firebaseInstance = new FirebaseSingleton();
export default firebaseInstance;